import type { RagAlert } from "../types/ragChatTypes";
import { ALERT_LEVEL_ORDER } from "../constants/ragUiConstants";
import {
  extractRawProcedureSections,
  isSafetyRailPreservedSectionTitle,
  normalizeAggregatedWiAlertText,
  procedureSectionKey,
  type RawProcedureSection,
} from "./ragChatUtils";

export type FlattenedPageAlert = RagAlert & { page: string; key: string };

/** Matches "**Warnings and Notes**" (or variants) as a section heading. */
const WARNINGS_SECTION_RE =
  /(?:^|\n)\s*\*{0,2}\s*warnings?\s*(?:and|\/|&)\s*notes?\s*:?\s*\*{0,2}\s*(?:\n|$)/i;

const POST_INSTALL_SECTION_RE =
  /(?:^|\n)\s*\*{0,2}\s*post[-\s]?install\s*\*{0,2}\s*:?\s*(?:\n|$)/i;

function normalizeAlertText(raw: string): string {
  return raw
    .replace(/^\*{1,2}|\*{1,2}$/g, "")
    .replace(/^[⚠️⛔⚡ℹ️📘📝•\-*]\s*/u, "")
    .trim();
}

function inferAlertLevel(line: string): string {
  const lower = line.toLowerCase();
  if (/^⛔|\bdanger\b/.test(line)) return "danger";
  if (/^⚠|warning|\bdo not\b|\bmust not\b/.test(lower)) return "warning";
  if (/^⚡|\bcaution\b/.test(lower)) return "caution";
  return "note";
}

/** Parse bullet lines from the LLM "Warnings and Notes" block. */
export function parseWarningsBlock(block: string): RagAlert[] {
  const alerts: RagAlert[] = [];
  const seen = new Set<string>();

  for (const line of block.split("\n")) {
    let trimmed = line.trim();
    if (!trimmed) continue;
    if (/^\*{0,2}\s*warnings?\s*(?:and|\/|&)\s*notes?\s*:?\s*\*{0,2}\s*$/i.test(trimmed)) {
      continue;
    }

    if (/^[-*•]\s+/.test(trimmed)) {
      trimmed = trimmed.replace(/^[-*•]\s+/, "");
    } else if (/^\d+[\.\)]\s+/.test(trimmed)) {
      continue;
    }

    const text = normalizeAlertText(trimmed);
    if (!text || text.length < 4) continue;

    const level = inferAlertLevel(trimmed);
    const key = `${level}:${text}`;
    if (seen.has(key)) continue;
    seen.add(key);
    alerts.push({ level, text });
  }
  return alerts;
}

export interface SplitAnswerResult {
  /** Answer body without trailing warnings (and without post-install block moved to end). */
  body: string;
  /** Alerts parsed from the dedicated warnings section in the answer. */
  extractedAlerts: RagAlert[];
  /** Trailing markdown after a horizontal rule (e.g. alternative-interpretation footer). */
  trailingCallout: string;
}

/**
 * Split LLM answer into main procedural text vs trailing "Warnings and Notes".
 * Backend prompts require warnings at the end of the answer string; the UI shows them first.
 *
 * Trailing `---` sections (linked procedure handoff, alternative interpretations) are
 * extracted from the full answer *before* the warnings split, because handoff answers
 * place warnings immediately before the horizontal rule.
 */
export function splitAnswerContent(answer: string): SplitAnswerResult {
  let text = (answer ?? "").trim();
  if (!text) {
    return { body: "", extractedAlerts: [], trailingCallout: "" };
  }

  let trailingCallout = "";
  const trailingHr = text.match(/\n---\n[\s\S]+$/);
  if (trailingHr?.index !== undefined) {
    trailingCallout = trailingHr[0].replace(/^\n---\n/, "").trim();
    text = text.slice(0, trailingHr.index).trim();
  }

  const warnMatch = text.match(WARNINGS_SECTION_RE);
  let beforeWarnings = text;
  let warningsTail = "";

  if (warnMatch && warnMatch.index !== undefined) {
    beforeWarnings = text.slice(0, warnMatch.index).trim();
    warningsTail = text.slice(warnMatch.index + warnMatch[0].length).trim();
  }

  const postMatch = beforeWarnings.match(POST_INSTALL_SECTION_RE);
  let mainBody = beforeWarnings;
  let postInstallBlock = "";

  if (postMatch && postMatch.index !== undefined) {
    mainBody = beforeWarnings.slice(0, postMatch.index).trim();
    postInstallBlock = beforeWarnings.slice(postMatch.index).trim();
  }

  const extractedAlerts = warningsTail ? parseWarningsBlock(warningsTail) : [];

  const body = [mainBody, postInstallBlock].filter(Boolean).join("\n\n").trim();

  return { body, extractedAlerts, trailingCallout };
}

/** True when the trailing callout is a cross-document handoff procedure block. */
export function isLinkedProcedureCallout(text: string): boolean {
  return /^#{1,3}\s*Linked Procedure\s*—/im.test(String(text ?? "").trim());
}

const SYNTHETIC_ALERT_PAGE = "0";

/** Merge API page_alerts with alerts extracted from answer markdown. */
export function mergePageAlerts(
  pageAlerts: Record<string, RagAlert[]>,
  extracted: RagAlert[]
): Record<string, RagAlert[]> {
  if (!extracted.length) return pageAlerts;

  const normalizeKey = (text: string) =>
    String(text ?? "")
      .replace(/^[-*•]\s+/, "")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();

  const merged: Record<string, RagAlert[]> = { ...pageAlerts };
  const seen = new Set(
    Object.values(merged)
      .flat()
      .map((a) => normalizeKey(a.text))
      .filter(Boolean)
  );

  const toAdd: RagAlert[] = [];
  for (const a of extracted) {
    const key = normalizeKey(a.text);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    toAdd.push(a);
  }

  if (toAdd.length) {
    merged[SYNTHETIC_ALERT_PAGE] = [...(merged[SYNTHETIC_ALERT_PAGE] ?? []), ...toAdd];
  }

  return merged;
}

/** Flatten page_alerts into a single list sorted by page then severity. */
export function flattenSortedPageAlerts(
  pageAlerts: Record<string, RagAlert[]>
): FlattenedPageAlert[] {
  const pages = Object.keys(pageAlerts).sort(
    (a, b) => (parseInt(a, 10) || 0) - (parseInt(b, 10) || 0)
  );
  const all = pages.flatMap((page) =>
    [...(pageAlerts[page] || [])]
      .sort((a, b) => (ALERT_LEVEL_ORDER[a.level] ?? 9) - (ALERT_LEVEL_ORDER[b.level] ?? 9))
      .map((a, i) => ({ ...a, page, key: `${page}-${i}` }))
  );
  return all.sort(
    (a, b) => (ALERT_LEVEL_ORDER[a.level] ?? 9) - (ALERT_LEVEL_ORDER[b.level] ?? 9)
  );
}

export function formatAlertPage(page?: string): string | undefined {
  if (!page || page === "0" || page === "?") return undefined;
  return page;
}

export function formatAlertSourceLine(
  page?: string,
  sourceDocumentName?: string,
  pageLabelShort = "p."
): string {
  const pageNum = formatAlertPage(page);
  const parts: string[] = [];
  if (sourceDocumentName?.trim()) parts.push(sourceDocumentName.trim());
  if (pageNum) parts.push(`${pageLabelShort}${pageNum}`);
  return parts.length ? parts.join(" · ") : "From source";
}

/** Remove trailing "Source: …" metadata sometimes bundled into alert text. */
export function stripEmbeddedAlertSource(text: string): string {
  const trimmed = String(text ?? "").trim();
  if (!trimmed) return "";

  const withoutSource = trimmed
    .replace(/\.Source:\s*.+$/i, ".")
    .replace(/\s+Source:\s*.+$/i, "")
    .replace(/Source:\s*.+$/i, "")
    .trim();

  return withoutSource || trimmed;
}

function formatRawSectionForAlert(sec: RawProcedureSection): string {
  const heading =
    sec.sectionNum != null
      ? `${sec.sectionNum}. ${sec.title}`
      : sec.headingKind === "atx"
        ? `### ${sec.title}`
        : `**${sec.title}**`;
  const body = String(sec.body ?? "").trim();
  return body ? `${heading}\n${body}` : heading;
}

/**
 * Whether a WI section may be stripped from pageAlerts when the same family
 * already appears in `answer`. Safety-rail families are never stripped by
 * answer overlap — warnings/notes must stay visible even when duplicated.
 */
function shouldStripDuplicatedProcedureSection(
  title: string,
  answerSectionKeys: Set<string>
): boolean {
  if (isSafetyRailPreservedSectionTitle(title)) return false;
  return answerSectionKeys.has(procedureSectionKey(title));
}

/**
 * Strip duplicated non-alert procedure sections (Tools, Steps, Purpose, …)
 * from a single pageAlert when those families already appear in `answer`.
 * Always preserves Safety Precautions / Warnings and Notes / Cautions /
 * Warnings, plus leading advisory prose and unique sections.
 * Returns null when nothing meaningful remains.
 */
export function sanitizeAlertTextForSafetyRail(
  text: string,
  answerSectionKeys: Set<string>
): string | null {
  const raw = String(text ?? "").trim();
  if (!raw) return null;

  const normalized = normalizeAggregatedWiAlertText(raw);
  const { intro, sections } = extractRawProcedureSections(normalized);

  // Case A — no recognizable WI structure: keep unchanged (incl. lone warnings).
  if (!sections.length) {
    return raw;
  }

  const kept = sections.filter(
    (s) => !shouldStripDuplicatedProcedureSection(s.title, answerSectionKeys)
  );

  // Nothing duplicated — avoid reformatting churn.
  if (kept.length === sections.length) {
    return raw;
  }

  const parts: string[] = [];
  if (intro.trim()) parts.push(intro.trim());
  for (const sec of kept) {
    parts.push(formatRawSectionForAlert(sec));
  }
  const out = parts.join("\n\n").trim();
  return out || null;
}

/**
 * Sanitize pageAlerts for the Safety notices rail: remove duplicated Tools /
 * Steps / other non-alert procedure sections already present in `answer`.
 * Never drops Safety Precautions / Warnings and Notes / Cautions merely
 * because the same text or section exists in `answer`.
 */
export function sanitizePageAlertsForSafetyRail(
  pageAlerts: Record<string, RagAlert[]> | null | undefined,
  answer: string
): Record<string, RagAlert[]> {
  if (!pageAlerts || !Object.keys(pageAlerts).length) return {};

  const answerKeys = new Set(
    extractRawProcedureSections(String(answer ?? "")).sections.map((s) =>
      procedureSectionKey(s.title)
    )
  );

  const out: Record<string, RagAlert[]> = {};
  for (const [page, items] of Object.entries(pageAlerts)) {
    const cleaned: RagAlert[] = [];
    for (const item of items ?? []) {
      const text = String(item?.text ?? "").trim();
      if (!text) continue;
      const sanitized = sanitizeAlertTextForSafetyRail(text, answerKeys);
      if (!sanitized) continue;
      cleaned.push({ ...item, text: sanitized });
    }
    if (cleaned.length) out[page] = cleaned;
  }
  return out;
}

/**
 * Safety notices rail alerts — mobile / web parity:
 * sanitized `page_alerts` + Warnings-and-Notes section + WI Safety Precautions.
 *
 * Mixed pageAlerts that embed Tools/Steps already present in `answer` are
 * sanitized first; Safety Precautions / warnings / notes stay on the rail.
 */
export function buildDisplayPageAlerts(
  answer: string,
  pageAlerts?: Record<string, RagAlert[]> | null,
  safetyPrecautionsExtract?: (body: string) => RagAlert[]
): Record<string, RagAlert[]> {
  const answerText = String(answer ?? "");
  const split = splitAnswerContent(answerText);
  const sanitizedAlerts = sanitizePageAlertsForSafetyRail(
    pageAlerts ?? {},
    answerText
  );
  const withWarnings = mergePageAlerts(sanitizedAlerts, split.extractedAlerts);
  const fromSafety = safetyPrecautionsExtract
    ? safetyPrecautionsExtract(split.body)
    : [];
  return mergePageAlerts(withWarnings, fromSafety);
}
